import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import PropTypes from "prop-types";
import {AButton, AIcon, AMenu} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import css from "./DropdownMenu.module.scss";

export const DropdownMenu = forwardRef(
  (
    {
      buttonClass,
      buttonContent,
      buttonProps,
      buttonSpanProps,
      buttonVariant,
      chevron = true,
      chevronSize = 12,
      children,
      closeMenuForItems = [],
      closeMenuOnClick = false,
      closeIcon = false,
      icon = false,
      iconLeft = false,
      menuClass,
      menuPlacement = "bottom-right",
      menuProps,
      onClose
    },
    ref
  ) => {
    const menuButtonRef = useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      closeMenu() {
        if (menuOpen) {
          handleButtonClick();
        }
      }
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const itemsToClose =
      closeMenuForItems && closeMenuForItems.length > 0
        ? JSON.stringify(closeMenuForItems)
        : [];

    // Handles closing specific menu items when the menu is not
    // set to closeOnClick
    const closeForItems = useCallback(() => {
      if (!closeMenuOnClick && closeMenuForItems.length > 0) {
        setMenuOpen(false);
      }
    }, [closeMenuOnClick, closeMenuForItems.length]);

    useEffect(() => {
      closeForItems();
    }, [itemsToClose, closeForItems]);

    const buttonClasses = classNames({
      "dropdown-button": true,
      active: menuOpen,
      [buttonClass]: buttonClass,
      "wm-hide": true,
      [css.selectVariant]: buttonVariant === "select"
    });

    const menuClasses = classNames({
      "dropdown-menu": true,
      open: menuOpen,
      closed: !menuOpen,
      [menuClass]: menuClass
    });

    const buttonStyleVariant =
      buttonVariant === "select"
        ? {secondary: true}
        : buttonVariant
        ? {[buttonVariant]: true}
        : {tertiary: true};

    const handleClose = () => {
      onClose?.();
      setMenuOpen(false);
    };

    const handleButtonClick = () => {
      setMenuOpen(!menuOpen);
    };

    const renderButtonIcon = (position) => (
      <AIcon
        className="dropdown-icon"
        {...{[position]: true}}
        size={chevronSize}
        data-testid={"DropdownMenu-Icon"}>
        {closeIcon && !menuOpen ? closeIcon : icon ? icon : "caret-down"}
      </AIcon>
    );

    const renderButton = (
      <AButton
        aria-haspopup="true"
        aria-expanded={menuOpen}
        className={buttonClasses}
        medium
        onClick={() => handleButtonClick()}
        ref={menuButtonRef}
        {...buttonProps}
        {...buttonStyleVariant}
        data-testid={
          buttonProps?.["data-testid"]
            ? buttonProps["data-testid"]
            : "DropdownMenu-Button"
        }>
        {chevron && iconLeft && renderButtonIcon("left")}
        <span
          {...buttonSpanProps}
          className={classNames({
            [buttonSpanProps?.className]: buttonSpanProps?.className
          })}>
          {buttonContent}
        </span>
        {chevron && !iconLeft && renderButtonIcon("right")}
      </AButton>
    );

    return (
      <>
        {renderButton}
        <AMenu
          anchorRef={menuButtonRef}
          className={menuClasses}
          closeOnClick={closeMenuOnClick}
          data-testid={
            menuProps?.["data-testid"]
              ? menuProps["data-testid"]
              : "DropdownMenu-Menu"
          }
          onClose={handleClose}
          open={menuOpen}
          placement={menuPlacement}
          {...menuProps}>
          {children}
        </AMenu>
      </>
    );
  }
);

DropdownMenu.propTypes = {
  /**
   * className for dropdown button
   */
  buttonClass: PropTypes.string,
  /**
   * Text or icon for dropdown button
   */
  buttonContent: PropTypes.any,
  /**
   * Used to pass in any other prop to the dropdown button (id, style, disabled,
   * type, href, etc.)
   */
  buttonProps: PropTypes.object,
  /**
   * Used to pass in any other prop to the dropdown button
   *  span (id, style, disabled, type, href, etc.)
   */
  buttonSpanProps: PropTypes.object,
  /**
   * 'primary', 'secondary', 'tertiary' & 'tertiaryAlt' sets the dropdown button
   * to the equivalent AButton style.
   * https://magna-react.vercel.app/components/button#variants
   *
   * "select" sets the dropdowns button style to that of an ASelect.
   * https://magna-react.vercel.app/components/select
   *
   * @defaultValue tertiary
   */
  buttonVariant: PropTypes.oneOf([
    "primary",
    "secondary",
    "select",
    "tertiary",
    "tertiaryAlt"
  ]),
  /**
   * Set to false to remove the dropdown button chevron icon
   *
   * @defaultValue true
   */
  chevron: PropTypes.bool,
  /**
   * @defaultValue 12
   */
  chevronSize: PropTypes.number,
  children: PropTypes.any,
  /**
   * Option to pass in a different button icon for the closed menu state
   * Icons: https://magna-react.vercel.app/components/icon#magna-icon-gallery
   */
  closeIcon: PropTypes.string,
  /**
   * Set to true to close the menu when any menu item is clicked
   *
   * @defaultValue false
   */
  closeMenuOnClick: PropTypes.bool,
  /**
   * Menu item values that should close the menu on click
   * (Only needs to be used when closeMenuOnClick is false)
   */
  closeMenuForItems: PropTypes.array,
  /**
   * Option to pass in a custom magna-react icon to use:
   * Icons: https://magna-react.vercel.app/components/icon#magna-icon-gallery
   *
   * @defaultValue caretDown
   */
  icon: PropTypes.string,
  /**
   * If true, button icon will be to the left of the button text
   *
   * @defaultValue false
   */
  iconLeft: PropTypes.bool,
  /**
   * If true, slide out drawer animation and styling will be applied
   *
   * @defaultValue false
   */
  leftDrawer: PropTypes.bool,
  /**
   * className for the menu
   */
  menuClass: PropTypes.string,
  /**
   * Used to pass in any other props to the dropdown menu (key, id, style, other
   * AMenu props, etc.)
   */
  menuProps: PropTypes.object,
  /**
   * Sets the placement of the dropdown menu under the dropdown button
   *
   * @defaultValue "bottom-right"
   */
  menuPlacement: PropTypes.oneOf([
    "top",
    "top-right",
    "right-top",
    "right",
    "right-bottom",
    "bottom-right",
    "bottom",
    "bottom-left",
    "left-bottom",
    "left",
    "left-top",
    "top-left"
  ])
};
